import dayjs from 'dayjs';
import {useSelector} from 'react-redux';
import {useQueryClient} from '@tanstack/react-query';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Alert, Grid, TextField, Typography, Box} from '@mui/material';
import {
  StepperLayout,
  useStepperState,
} from '../../../../components/layouts/stepper/StepperLayout';
import {IStation} from '../../../../dto/Station.dto';
import TimedMsgRequestContentTypeSwitchForm from './contents/TimedMsgRequestContentTypeSwitchForm';
import TimedMsgRequestContents from './contents/TimedMsgRequestContents';
import {iTimedMessageMessageItem} from './contents/TimedMsgRequestContentMessageCard';
import TimedMsgRequestDays from './days/TimedMsgRequestDays';
import TimedMsgRequestTimes, {
  MultipleDateSelectTimeCmpt,
} from './times/TimedMsgRequestTimes';
import {iTimedMsgTimesItem} from './times/TimedMsgRequestTimesCard';
import TimedMsgRequestReview, {
  TimedMessageDetails,
} from './review/TimedMsgRequestReview';
import {apiPost} from '../../../../axiosConfig';
import {useStoreSelector} from '../../../../store/configstore';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import {
  ITimedMessageDayType,
  ITimedMessageMsgType,
} from '../../../../dto/timed-message/TimedMessageRequest.dto';
import {companyIdSelector} from '../../../../store/Slices/companySlice';
import StepperCard from '../../../../components/layouts/stepper/StepperCard';
import StationsDropDown from '../../../../components/models/stations/StationDropDown';
import {queryKeyMessageRequestList} from '../../../../react_query/messages/MessagesByCmpId';
import {isValidTimedMsgDates} from './TimedMsgFunctions';
import {getTimedMsgCalendarApiPath} from '../../../../react_query/messages/timed-messages/TimedMsgCalendar';
import {isInternalSelector} from '../../../../store/Slices/userSlice';
import {
  GetDaysFromMultipleDateRange,
  isAllDaysTimed,
} from '../../../../components/utlis/date-time/GetDaysInDateRange';
import TimedMsgRequestReviewDates from './review/TimedMsgRequestReviewDates';
import TimedMsgRequestReviewDayTimes from './review/TimedMsgRequestReviewDayTimes';
import TimedMsgRequestContentMessageForm from './contents/TimedMsgRequestContentMessageForm';
import {getTimedMsgOnDateApiPath} from '../../../../react_query/messages/timed-messages/TimedMsgOnDate';
import timedMsgTimeDuplicationValidation from './TimedExistenceValidation';

type Props = {
  defaultName: string;
  defaultStep: number;
  defaultStations: IStation[];
  defaultMessages: iTimedMessageMessageItem[];
  defaultDayType: ITimedMessageDayType;
  defaultDateRanges: iTimedMsgDateRange[];
  defaultTimeItems: iTimedMsgTimesItem[];
  defaultContentType: ITimedMessageMsgType;
  onClose: () => void;
  restoreId?: number;
  updateId?: number;
  isCopyForm?: boolean;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  messageStatus: string | null;
  repository?: boolean;
};

export type ITimedDuplicateTimes = {
  date: dayjs.Dayjs;
  times: string[];
};

export interface iTimedMsgDateRange {
  id: number;
  start_date: dayjs.Dayjs | null;
  end_date: dayjs.Dayjs | null;
  chosenDays?: string[];
  day_times?: iTimedMsgTimesItem[];
  isError?: boolean;
  excluded_dates?: {
    id: number;
    excluded_date: dayjs.Dayjs | null;
  }[];
}

const TimedMessageRequest = ({
  defaultName,
  defaultStep,
  defaultStations,
  defaultMessages,
  defaultDayType,
  defaultDateRanges,
  defaultTimeItems,
  defaultContentType,
  onClose,
  restoreId,
  updateId,
  isCopyForm,
  setStatus,
  messageStatus,
  repository,
}: Props) => {
  const isStaff = useSelector(isInternalSelector);
  const {activeStepIndex, onNextStep, onPreviousStep, onStepClick} =
    useStepperState(defaultStep);
  const [selectedStations, setSelectedStations] =
    useState<IStation[]>(defaultStations);
  const [messages, setMessages] =
    useState<iTimedMessageMessageItem[]>(defaultMessages);
  const [contentsType, setContentsType] =
    useState<ITimedMessageMsgType>(defaultContentType);
  const [dayType, setDayType] = useState(defaultDayType);
  const [dateRanges, setDateRanges] = useState(defaultDateRanges);
  const [daysTimes, setDaysTimes] = useState(defaultTimeItems);
  const [selectedWeekdays, setSelectedWeekdays] = useState<string[]>([]);
  const [apiState, setApiState] = useState({status: '', error: null});
  const companyId = useStoreSelector(companyIdSelector);
  const [messageName, setMessageName] = useState<string>(defaultName);
  const [footerText, setFooterText] = useState<string>('');
  const [duplicateTimes, setDuplicateTimes] = useState<ITimedDuplicateTimes[]>(
    [],
  );
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isTimeUniqueLoading, setIsTimeUniqueLoading] = useState(false);
  const queryClient = useQueryClient();

  const handleTypeChange = useCallback((newDayType: ITimedMessageDayType) => {
    setDayType(newDayType);
    setDateRanges([
      {
        id: -1 * Date.now(),
        start_date: null,
        end_date: null,
        chosenDays: [],
      } as iTimedMsgDateRange,
    ]);
    setDaysTimes([
      {
        id: -1 * Date.now(),
        availableDays: [],
        selectedDays: [],
        timeRanges: [],
      },
    ]);
  }, []);

  const handleSubmit = useCallback(() => {
    setApiState({status: 'pending', error: null});
    const url = updateId
      ? `/v1/company/${companyId}/message/timed-message/${updateId}/update?_method=PUT`
      : `/v1/company/${companyId}/message/timed-message`;
    const fd = new FormData();
    selectedStations.forEach((station, i) =>
      fd.append(`stations[${i}]`, station.id.toString()),
    );
    fd.set('message_type', contentsType);
    fd.set('message_name', messageName);
    messages.forEach((item, i) => {
      fd.append(`sequence[${i}][type]`, item.type);
      fd.append(`sequence[${i}][id]`, `${item.id}`);
      fd.append(`sequence[${i}][isNew]`, `${item.isNew ? 1 : 0}`);
      if (item.type === 'song') {
        if (item.file) {
          fd.set(`sequence[${i}][type]`, 'file');
          fd.append(`sequence[${i}][file]`, item.file);
        } else if (item.track) {
          fd.append(`sequence[${i}][track_id]`, item.track.id.toString());
        } else {
          fd.append(`sequence[${i}][artist]`, item.artist);
          fd.append(`sequence[${i}][title]`, item.title);
        }
      } else {
        fd.append(`sequence[${i}][message]`, item.message);
        fd.append(
          `sequence[${i}][track_id]`,
          item.track ? item.track.id.toString() : '0',
        );
      }
    });
    fd.set('day_type', dayType);
    dateRanges.forEach((range, i) => {
      fd.append(`dates[${i}][id]`, range.id.toString());
      fd.append(
        `dates[${i}][start_date]`,
        range.start_date ? range.start_date.format('YYYY-MM-DD') : 'null',
      );
      fd.append(
        `dates[${i}][end_date]`,
        range.end_date ? range.end_date.format('YYYY-MM-DD') : 'null',
      );

      if (range.day_times && dayType === 'multiple') {
        range?.day_times.forEach((val, j) => {
          val.selectedDays.forEach((data, k) => {
            fd.append(
              `dates[${i}][day_times][${j}][days][${k}]`,
              data.toLowerCase(),
            );
          });
          val.timeRanges.forEach((data, k) => {
            fd.append(
              `dates[${i}][day_times][${j}][times][${k}]`,
              data.start_time ? data.start_time.format('HH:mm:00') : 'null',
            );
          });
        });
      }
    });

    if (dayType !== 'multiple') {
      daysTimes.forEach((item, i) => {
        item.selectedDays.forEach((day, j) => {
          fd.append(`day_times[${i}][days][${j}]`, day.toLowerCase());
        });
        item.timeRanges.forEach((range, j) => {
          fd.append(
            `day_times[${i}][times][${j}]`,
            range.start_time ? range.start_time.format('HH:mm:00') : 'null',
          );
        });
      });
    }

    apiPost(url, fd)
      .then(() => {
        setStatus('Active');

        const keysToInvalidate = [
          [queryKeyMessageRequestList(companyId, 'timed-message')],
          [getTimedMsgCalendarApiPath(companyId)],
          [getTimedMsgOnDateApiPath(companyId)],
        ];

        keysToInvalidate.forEach(key => {
          queryClient.invalidateQueries({
            queryKey: key,
            refetchType: 'active',
          });
        });
        setApiState({status: 'success', error: null});
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  }, [
    updateId,
    companyId,
    selectedStations,
    contentsType,
    messageName,
    messages,
    dayType,
    dateRanges,
    daysTimes,
    queryClient,
    setStatus,
  ]);

  const handleRestore = useCallback(() => {
    setApiState({status: 'pending', error: null});
    const dayTimes = daysTimes.map(o => ({
      days: o.selectedDays.map(day => day.toLowerCase()),
      times: o.timeRanges.map(o =>
        o.start_time ? o.start_time.format('HH:mm:00') : null,
      ),
    }));
    const payload = {
      type: 'Active',
      day_type: dayType,
      day_times: dayTimes,
      dates: dateRanges.map(({start_date, end_date, day_times}) => ({
        start_date: start_date ? start_date.format('YYYY-MM-DD') : null,
        end_date: end_date ? end_date.format('YYYY-MM-DD') : null,
        day_times: day_times?.map(o => ({
          days: o.selectedDays.map(day => day.toLowerCase()),
          times: o.timeRanges.map(o =>
            o.start_time ? o.start_time.format('HH:mm:00') : null,
          ),
        })),
      })),
    };
    apiPost(
      `v1/company/${companyId}/message/timed-message/${restoreId}?_method=PUT`,
      payload,
    )
      .then(() => {
        setApiState({status: 'success', error: null});
        queryClient.invalidateQueries({
          queryKey: [queryKeyMessageRequestList(companyId, 'timed-message')],
          refetchType: 'active',
        });
      })
      .catch(error => {
        setApiState({status: 'error', error});
      });
  }, [companyId, dateRanges, dayType, daysTimes, queryClient, restoreId]);

  // option to edit the form when api is failed
  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);

  const isStationSelected = Boolean(selectedStations.length);

  const isMessageNameGiven =
    messageName?.trim().length > 0 && messageName?.trim().length <= 50;

  const isDaySelectionEnabled = useCallback(() => {
    return (
      messages.length &&
      messages.every(message => {
        if (message.isNew) {
          if (message.type === 'message') {
            return (
              message.message.replaceAll('\n', '').trim().length >= 10 ||
              message.track
            );
          }
          if (
            message.file &&
            message?.fileDuration &&
            message?.fileDuration / 60 <= 5
          ) {
            return true;
          }
          if (message.track) return true;
          return (
            message.artist.trim() &&
            !message.artistErrorMsg &&
            message.title.trim() &&
            !message.titleErrorMsg
          );
        }
        return true;
      })
    );
  }, [messages]);

  const isTimeSelectionEnabled = useMemo(() => {
    return isValidTimedMsgDates(dateRanges, dayType, isStaff);
  }, [dayType, dateRanges, isStaff]);

  const daysInSelectedDates = useMemo(
    () => GetDaysFromMultipleDateRange(dateRanges),
    [dateRanges],
  );

  const isAllDaysSelected = useMemo(() => {
    return daysInSelectedDates.every(d => selectedWeekdays.includes(d));
  }, [daysInSelectedDates, selectedWeekdays]);

  const isReviewEnabled = useCallback(() => {
    if (dayType === 'one-day' && !isAllDaysSelected) {
      return false;
    }
    if (dayType === 'multiple') {
      return dateRanges.every(
        val =>
          !val.isError &&
          val.day_times?.every(
            val => val.selectedDays.length > 0 && val.timeRanges.length > 0,
          ) &&
          isAllDaysTimed(val),
      );
    }
    return daysTimes.every(
      timeItem =>
        timeItem.selectedDays.length &&
        timeItem.timeRanges.length &&
        timeItem.timeRanges.every(timeRange => timeRange.start_time),
    );
  }, [dateRanges, dayType, daysTimes, isAllDaysSelected]);

  const steps = useMemo(() => {
    const stepList = [
      {
        label: 'Select Stations & Message Type',
        disabled: false,
      },
      {
        label: 'Message Details',
        disabled: !isStationSelected || !isMessageNameGiven,
      },
    ];
    const restoreList = [
      {
        label: 'Select Date',
        disabled: restoreId ? false : !isDaySelectionEnabled(),
      },
      {
        label: 'Select Time',
        disabled: !isTimeSelectionEnabled,
      },
    ];
    const reviewStep = [
      {
        label: 'Review',
        disabled:
          !isReviewEnabled() || !!duplicateTimes.length || isTimeUniqueLoading,
      },
    ];
    if (restoreId) {
      return [...restoreList, ...reviewStep];
    }
    return [...stepList, ...restoreList, ...reviewStep];
  }, [
    isStationSelected,
    isMessageNameGiven,
    restoreId,
    isDaySelectionEnabled,
    isTimeSelectionEnabled,
    isReviewEnabled,
    duplicateTimes.length,
    isTimeUniqueLoading,
  ]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setMessageName(event.target.value);
    },
    [],
  );

  useEffect(() => {
    const flattenedWeeks = daysTimes.map(o => o.selectedDays).flat();
    const uniqueWeeks = flattenedWeeks.filter(
      (item, index, array) => array.indexOf(item) === index,
    );
    setSelectedWeekdays(uniqueWeeks);
  }, [daysTimes]);

  const title = useMemo(() => {
    let label = 'Create New Message';

    if (restoreId) {
      label = 'Restore timed message';
    } else if (isCopyForm && !repository) {
      label = 'Duplicate timed message';
    } else if (updateId) {
      label = 'Update timed message';
    }

    return label;
  }, [restoreId, isCopyForm, updateId, repository]);

  // Replace the problematic useEffect with this:
  useEffect(() => {
    // Only run once during component mount
    if (hasInitialized) return;

    const isDaysTimesValid = daysTimes.length > 0;
    const isSingleTempItem =
      daysTimes.length === 1 && daysTimes[0] && daysTimes[0]?.id < 0;

    // for cloning timed message functionality
    if (dateRanges.length === 0 && isDaysTimesValid && !isSingleTempItem) {
      setDaysTimes([
        {
          id: -Date.now(),
          availableDays: [],
          selectedDays: [],
          timeRanges: [],
        },
      ]);
    }

    setHasInitialized(true);
  }, [dateRanges.length, daysTimes.length, hasInitialized, daysTimes]);

  useEffect(() => {
    if (isReviewEnabled()) {
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => {
        timedMsgTimeDuplicationValidation(
          companyId,
          dateRanges,
          daysTimes,
          setDuplicateTimes,
          dayType,
          selectedStations,
          setIsTimeUniqueLoading,
          restoreId,
          updateId,
          abortController,
        );
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
        abortController.abort();
      };
    }
    return;
  }, [
    companyId,
    dateRanges,
    dayType,
    daysTimes,
    isReviewEnabled,
    restoreId,
    selectedStations,
    updateId,
  ]);

  const label = steps?.[activeStepIndex]?.label ?? '';
  const maxWidth = useMemo(() => {
    if (
      (label === 'Message Details' && messageStatus === 'In Progress') ||
      label === 'Select Date' ||
      label === 'Select Time'
    ) {
      return '80%';
    }
    return undefined;
  }, [label, messageStatus]);

  const handleMessageChange = useCallback((id: number, text: string) => {
    setMessages(prev =>
      prev.map(val => (val.id === id ? {...val, message: text} : val)),
    );
  }, []);

  const finalMaxWidth = useMemo(() => {
    if (updateId) return maxWidth;
    if (dayType === 'multiple') return '80%';
    if (
      (contentsType === 'song' || contentsType === 'sequence') &&
      label === 'Message Details'
    )
      return '100%';
    return undefined;
  }, [updateId, maxWidth, dayType, contentsType, label]);

  return (
    <StepperLayout
      steps={steps}
      activeStepIndex={activeStepIndex}
      onBack={onPreviousStep}
      onNext={onNextStep}
      onCancel={onClose}
      onSubmit={restoreId ? handleRestore : handleSubmit}
      onStepClick={onStepClick}
      title={title}
      isSubmitted={apiState.status !== ''}
      footerLabel={footerText}
      loading={apiState.status === 'pending' || isTimeUniqueLoading}
      maxWidth={finalMaxWidth}>
      {(() => {
        const label = steps?.[activeStepIndex]?.label ?? '';
        switch (label) {
          case 'Select Stations & Message Type':
            return (
              <StepperCard title='Select Options'>
                <Grid container spacing={4}>
                  <Grid item xs={5}>
                    {messageName?.trim().length > 50 && (
                      <Alert severity='error' sx={{mb: 1}}>
                        Name must be below 50 characters
                      </Alert>
                    )}
                    <TextField
                      className='min-h-40'
                      id='timed-message-name'
                      placeholder='Message Name'
                      variant='outlined'
                      value={messageName}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <StationsDropDown
                      stations={selectedStations}
                      setStations={setSelectedStations}
                      fetchEnable={true}
                    />
                  </Grid>
                </Grid>
                <TimedMsgRequestContentTypeSwitchForm
                  type={contentsType}
                  onChange={setContentsType}
                  setMessages={setMessages}
                  isCopyForm={isCopyForm}
                  isUpdate={Boolean(updateId)}
                  defaultContentType={defaultContentType}
                  messageStatus={messageStatus}
                />
              </StepperCard>
            );

          case 'Message Details':
            return isCopyForm ||
              messageStatus === 'Active' ||
              messageStatus === 'Inactive' ? (
              <>
                <Typography variant='body2' className='mb-4'>
                  Messages/Song Details
                </Typography>
                {messages.map(item => (
                  <Box key={item.id} mb={2}>
                    <TimedMessageDetails message={item} />
                    <TimedMsgRequestContentMessageForm
                      value={item.message}
                      id={item.id}
                      onChange={text => handleMessageChange(item.id, text)}
                      disabled
                    />
                  </Box>
                ))}
              </>
            ) : (
              <TimedMsgRequestContents
                type={contentsType}
                messages={messages}
                setMessages={setMessages}
                setFooterText={setFooterText}
                selectedStations={selectedStations}
                defaultMessages={updateId ? defaultMessages : undefined}
              />
            );

          case 'Select Date':
            return (
              <Grid container spacing={1} className='h-full'>
                <Grid item md={defaultMessages ? 9 : 12}>
                  <TimedMsgRequestDays
                    dayType={dayType}
                    onChange={handleTypeChange}
                    dates={dateRanges}
                    setDateRanges={setDateRanges}
                    setFooterText={setFooterText}
                    isToday={!isTimeSelectionEnabled}
                  />
                </Grid>
                {updateId && (
                  <Grid item md={3}>
                    <Typography className='text-20 font-bold' align='center'>
                      Existing Dates
                    </Typography>
                    <Typography className='mt-3'>
                      {defaultDayType === 'no-end'
                        ? 'No End Date'
                        : defaultDayType === 'one-day'
                        ? 'One Day'
                        : 'Multiple Days'}
                    </Typography>
                    <TimedMsgRequestReviewDates
                      type={defaultDayType}
                      ranges={defaultDateRanges}
                    />
                  </Grid>
                )}
              </Grid>
            );

          case 'Select Time':
            return dayType === 'multiple' ? (
              <MultipleDateSelectTimeCmpt
                dateRanges={dateRanges}
                setDateRanges={setDateRanges}
                duplicateTimes={duplicateTimes}
                updateId={updateId}
                defaultDateRanges={defaultDateRanges}
                dayType={dayType}
              />
            ) : (
              <Grid container spacing={1} className='h-full w-full'>
                <Grid item md={updateId ? 9 : 12} className='h-full w-full'>
                  <TimedMsgRequestTimes
                    items={daysTimes}
                    enableWeekdays
                    setDaysTimes={setDaysTimes}
                    selectedWeekdays={selectedWeekdays}
                    isAddSlot={
                      !isAllDaysSelected && daysInSelectedDates.length > 1
                    }
                    duplicateTimes={duplicateTimes}
                    availableDays={daysInSelectedDates}
                    dayType={dayType}
                  />
                </Grid>
                {updateId && (
                  <Grid item md={3}>
                    <Typography className='text-20 font-bold' align='center'>
                      Existing Time
                    </Typography>
                    <TimedMsgRequestReviewDayTimes items={defaultTimeItems} />
                  </Grid>
                )}
              </Grid>
            );

          case 'Review':
            return (
              <TimedMsgRequestReview
                stations={selectedStations}
                messageName={messageName}
                messages={messages}
                dayType={dayType}
                dateRanges={dateRanges}
                daysTimes={daysTimes}
                restoreId={restoreId}
              />
            );
          default:
            return null;
        }
      })()}

      {activeStepIndex === 4 && (
        <Typography variant='h6' className='mt-5 italic text-white-50 text-13'>
          Please choose the time when the message needs to play
        </Typography>
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={restoreId ? handleRestore : handleSubmit}
          onEdit={handleEdit}
          onClose={onClose}
        />
      )}
    </StepperLayout>
  );
};

export default TimedMessageRequest;
